import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  fetchRetentionPreview,
  listSystemSettings,
  updateSystemSetting,
  type RetentionPreview,
} from '@/lib/api-client';
import { useAuth } from '@/app/auth-context';

const RETENTION_KEYS = [
  'retention_active_client_years',
  'retention_archived_client_years',
  'retention_audit_log_months',
] as const;
type RetentionKey = (typeof RETENTION_KEYS)[number];

const CAPS: Record<RetentionKey, { min: number; max: number; unit: 'years' | 'months' }> = {
  retention_active_client_years: { min: 1, max: 5, unit: 'years' },
  retention_archived_client_years: { min: 1, max: 5, unit: 'years' },
  retention_audit_log_months: { min: 6, max: 60, unit: 'months' },
};

export function AdminRetentionPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();

  const [values, setValues] = useState<Record<RetentionKey, string>>({
    retention_active_client_years: '3',
    retention_archived_client_years: '3',
    retention_audit_log_months: '24',
  });
  const [original, setOriginal] = useState<Record<RetentionKey, string>>(values);
  const [preview, setPreview] = useState<RetentionPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      const [settings, prev] = await Promise.all([
        listSystemSettings(accessToken),
        fetchRetentionPreview(accessToken).catch(() => null),
      ]);
      const map: Record<RetentionKey, string> = {
        retention_active_client_years: '3',
        retention_archived_client_years: '3',
        retention_audit_log_months: '24',
      };
      for (const row of settings.data) {
        if ((RETENTION_KEYS as readonly string[]).includes(row.setting_key)) {
          map[row.setting_key as RetentionKey] = row.setting_value;
        }
      }
      setValues(map);
      setOriginal(map);
      if (prev) setPreview(prev);
    } catch (cause) {
      setError(messageFor(cause, t));
    } finally {
      setBusy(false);
    }
  }, [accessToken, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasChanges = (Object.keys(values) as RetentionKey[]).some(
    (k) => values[k] !== original[k],
  );

  const save = useCallback(async () => {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      for (const key of RETENTION_KEYS) {
        if (values[key] !== original[key]) {
          await updateSystemSetting(accessToken, key, values[key]);
        }
      }
      setOriginal(values);
      setSavedAt(new Date().toLocaleTimeString());
      const fresh = await fetchRetentionPreview(accessToken).catch(() => null);
      if (fresh) setPreview(fresh);
    } catch (cause) {
      setError(messageFor(cause, t));
    } finally {
      setBusy(false);
    }
  }, [accessToken, original, t, values]);

  if (!user || user.role !== 'head_coach') {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8">
        <p role="alert" className="form-error">
          {t('admin.retention.errors.headCoachOnly')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('admin.retention.title')}</h1>
        <p className="mt-s3 text-gray-2">{t('admin.retention.subtitle')}</p>
      </header>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || !hasChanges) return;
          void save();
        }}
        className="space-y-s4"
        data-testid="retention-form"
      >
        {RETENTION_KEYS.map((key) => {
          const cap = CAPS[key];
          return (
            <div key={key}>
              <label htmlFor={key} className="form-label">
                {t(`admin.retention.fields.${key}.label`)}
              </label>
              <input
                id={key}
                type="number"
                min={cap.min}
                max={cap.max}
                step={1}
                className="input-field"
                value={values[key]}
                onChange={(e) => {
                  setValues({ ...values, [key]: e.target.value });
                }}
                data-testid={`retention-input-${key}`}
              />
              <p className="form-help mt-s1 text-sm text-gray-2">
                {t('admin.retention.fields.bounds', {
                  min: cap.min,
                  max: cap.max,
                  unit: t(`admin.retention.units.${cap.unit}`),
                })}
              </p>
            </div>
          );
        })}

        {error && (
          <p role="alert" className="form-error" data-testid="retention-error">
            {error}
          </p>
        )}
        {savedAt && (
          <p role="status" className="text-flame-deep" data-testid="retention-saved-at">
            {t('admin.retention.savedAt', { time: savedAt })}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary"
          disabled={busy || !hasChanges}
          data-testid="retention-save"
        >
          {busy ? t('admin.retention.saving') : t('admin.retention.save')}
        </button>
      </form>

      <hr className="my-s6 border-bone-3" />

      <section data-testid="retention-preview">
        <h2 className="font-display text-display-sm text-ink mb-s3">
          {t('admin.retention.preview.title')}
        </h2>
        {!preview ? (
          <p className="text-gray-2">{t('admin.retention.preview.loading')}</p>
        ) : (
          <div className="space-y-s3">
            <PreviewRow
              label={t('admin.retention.preview.activeClient')}
              dueLabel={t('admin.retention.preview.dueNow')}
              upcomingLabel={t('admin.retention.preview.upcoming')}
              due={preview.due.activeClient}
              upcoming={preview.upcoming.activeClient}
            />
            <PreviewRow
              label={t('admin.retention.preview.archivedClient')}
              dueLabel={t('admin.retention.preview.dueNow')}
              upcomingLabel={t('admin.retention.preview.upcoming')}
              due={preview.due.archivedClient}
              upcoming={preview.upcoming.archivedClient}
            />
            <PreviewRow
              label={t('admin.retention.preview.auditLog')}
              dueLabel={t('admin.retention.preview.dueNow')}
              upcomingLabel={t('admin.retention.preview.upcoming')}
              due={preview.due.auditLog}
              upcoming={preview.upcoming.auditLog}
            />
          </div>
        )}
      </section>
    </section>
  );
}

interface PreviewRowProps {
  label: string;
  dueLabel: string;
  upcomingLabel: string;
  due: number;
  upcoming: number;
}

function PreviewRow({ label, dueLabel, upcomingLabel, due, upcoming }: PreviewRowProps): JSX.Element {
  return (
    <div className="flex justify-between bg-bone-2 px-s3 py-s2 border border-bone-3">
      <span className="font-medium">{label}</span>
      <span className="text-sm text-gray-2">
        {dueLabel}: <span className="font-mono text-ink">{due}</span>
        {' · '}
        {upcomingLabel}: <span className="font-mono text-ink">{upcoming}</span>
      </span>
    </div>
  );
}

function messageFor(cause: unknown, t: (k: string) => string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 400) {
      return cause.envelope.message;
    }
    if (cause.status === 403) {
      return t('admin.retention.errors.headCoachOnly');
    }
    if (cause.status === 0) {
      return t('admin.retention.errors.network');
    }
    return cause.envelope.message;
  }
  return t('admin.retention.errors.network');
}
